import { useCallback, useEffect, useMemo, useState } from "react"
import FupQuotaService from "../../../Service/API/FupQuota/FupQuotaService"
import { ByteFormatter, DateFormatter, fIDR } from "../../../Service/Helper/Utils"
import { PaymentMethodPickerAddOn } from "./Child/PaymentMethodPickerAddOn"

const TABS = [
  { key: "QUOTA", label: "Kuota", icon: "fa-database" },
  { key: "INTERNET", label: "Internet", icon: "fa-gauge-high" }
]

const addOnMetric = (option = {}) => {
  if (option.addOnType === "QUOTA" || option.isFups) return `${ByteFormatter(option.fupQuota)} kuota tambahan`
  if (option.addOnType === "INTERNET" || option.isBandwithAddOn) return `${option.speedDownlink || 0}/${option.speedUplink || 0} Kbps`
  return option.addOnTimespan ? `Recurring ${option.addOnTimespan} bulan` : "Recurring bulanan"
}

const paymentLabel = (purchase = {}) => (purchase.paymentMethod || purchase.payment?.method || "QRIS")

export const FupQuotaContent = ({ data }) => {
  const profile = data?.profile ?? {}
  const enabled = data?.serviceStatus?.fup?.visible === true || profile?.enableFupSupport === true

  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [options, setOptions] = useState([])
  const [purchases, setPurchases] = useState([])
  const [tab, setTab] = useState("QUOTA")
  const [selectedId, setSelectedId] = useState("")
  const [activePurchase, setActivePurchase] = useState({})
  const [pendingPurchase, setPendingPurchase] = useState({}) // authoritative "unpaid" state from the server
  const [browsing, setBrowsing] = useState(false) // lets user look at packages while a payment is pending
  const [error, setError] = useState("")

  const fupQuotaService = useMemo(() => new FupQuotaService(), [])

  // Derived data
  const filteredOptions = useMemo(
    () => options.filter(option => (option.addOnType || "QUOTA") === tab),
    [options, tab]
  )

  const availableTabs = useMemo(
    () => TABS.filter(t => options.some(option => (option.addOnType || "QUOTA") === t.key)),
    [options]
  )

  const selectedPackage = useMemo(
    () => filteredOptions.find(option => option.id === selectedId),
    [filteredOptions, selectedId]
  )

  const hasActivePurchase = Object.keys(activePurchase).length > 0
  const hasPendingPurchase = pendingPurchase && Object.keys(pendingPurchase).length > 0

  // activePurchase takes priority (e.g. user clicked "Lihat Detail"), fallback to pending
  const paymentTarget = hasActivePurchase ? activePurchase : pendingPurchase
  const showPaymentScreen = !!paymentTarget?.id && !browsing

  // Locate the original option to display rich metrics if the purchase object is missing them
  const targetOptionDetails = useMemo(() => {
    return options.find(o => o.id === paymentTarget?.addOnId) || paymentTarget || {}
  }, [options, paymentTarget])

  // Data Loading (Supports silent polling)
  const loadData = useCallback(async (silent = false) => {
    if (!enabled) return
    try {
      if (!silent) setLoading(true)
      if (!silent) setError("")
      
      const [optionResult, purchaseResult, stateResult] = await Promise.all([
        fupQuotaService.getOptions(),
        fupQuotaService.getPurchases(),
        fupQuotaService.getState()
      ])
      
      const packageOptions = (optionResult?.data?.options ?? []).filter(
        opt => opt.addOnType === "QUOTA" || opt.addOnType === "INTERNET"
      )
      const purchaseRows = purchaseResult?.data ?? []
      const pending = stateResult?.data ?? purchaseRows.find(item => !item.isPaid) ?? null
      // console.log(packageOptions)
      setOptions(packageOptions)
      setPurchases(purchaseRows)
      setPendingPurchase(pending)

      // Sync activePurchase state if the user is currently viewing it (e.g., changes from unpaid to paid)
      setActivePurchase(prev => {
        if (!prev?.id) return prev
        const updated = purchaseRows.find(p => p.id === prev.id)
        return updated || prev
      })

    } catch (err) {
      if (!silent) setError(err?.response?.data?.message ?? "Gagal memuat paket Add-On")
    } finally {
      if (!silent) setLoading(false)
    }
  }, [enabled, fupQuotaService])

  // Initial load and Pooler setup
  useEffect(() => {
    loadData()
    // Poll every 10 seconds silently to sync state
    const pooler = setInterval(() => {
      loadData(true)
    }, 10000)
    
    return () => clearInterval(pooler)
  }, [loadData])

  // Land on whichever tab actually has packages
  useEffect(() => {
    if (availableTabs.length && !availableTabs.some(t => t.key === tab)) {
      setTab(availableTabs[0].key)
    }
  }, [availableTabs, tab])

  // Reset the selected card whenever the tab changes
  useEffect(() => {
    setSelectedId("")
  }, [tab])

  // Actions
  const createPurchase = async () => {
    if (!selectedId) return
    try {
      setSubmitting(true)
      setError("")
      const result = await fupQuotaService.createPurchase({
        addOnId: selectedId,
        description: `Pembelian ${selectedPackage?.addOnTypeLabel || "Add-On"} ${selectedPackage?.name ?? ""}`.trim()
      })
      const purchase = result?.data
      if (purchase?.id) {
        setPurchases(prev => [purchase, ...prev.filter(item => item.id !== purchase.id)])
        setActivePurchase(purchase)
        setPendingPurchase(purchase)
        setBrowsing(false)
        setSelectedId("")
      }
    } catch (err) {
      setError(err?.response?.data?.message ?? "Gagal membuat transaksi Add-On")
    } finally {
      setSubmitting(false)
    }
  }

  const openPaymentFor = async (purchase) => {
    setActivePurchase(purchase)
    setBrowsing(false)
    try {
      const result = await fupQuotaService.getPurchase(purchase.id)

      window.scrollTo({ top: 0, behavior: "smooth" })
      if (result?.data) setActivePurchase(result.data)
    } catch {
      // keep the row we already have if the refresh fails
    }
  }

  const handleClosePaymentScreen = () => {
    setBrowsing(true)
    // If user was viewing a past paid transaction, clear it out when they leave
    if (paymentTarget?.isPaid) {
      setActivePurchase({})
    }
  }

  // Render guards
  if (!enabled) {
    return (
      <div className="card fup-locked">
        <div className="card-body fup-locked__body">
          <i className="fa fa-lock fup-locked__icon" />
          <h5 className="fup-locked__title">Paket tidak tersedia</h5>
          <p className="fup-locked__desc"></p>
        </div>
      </div>
    )
  }

  return (
    <div className="fup">
      {error && <div className="fup-alert">{error}</div>}

      {/* Payment / Detail Screen (priority view) */}
      {showPaymentScreen && (
        <div className="card fup-pending">
          <div className="fup-pending__header">
            <span className="fup-pending__title">
              {paymentTarget.isPaid ? (
                <><i className="fa fa-check-circle text-success" /> Detail Transaksi</>
              ) : (
                <><i className="fa fa-clock" /> Menunggu Pembayaran</>
              )}
            </span>
            <button type="button" className="fup-pending__switch" onClick={handleClosePaymentScreen}>
              {paymentTarget.isPaid ? "Tutup Detail" : "Lihat paket lain"}
            </button>
          </div>
          <div className="fup-pending__summary">
            <div>
              <div className="fup-pending__name">{paymentTarget.addOnName}</div>
              <div className="fup-pending__meta">
                {paymentTarget.addOnTypeLabel || "Add-On"} · {paymentLabel(paymentTarget)}
              </div>
              <div className="fup-pending__details mt-1 text-muted" style={{ fontSize: "13px" }}>
                <i className="fa fa-bolt me-1" /> {addOnMetric(targetOptionDetails)}
              </div>
            </div>
            <div className="text-end">
              <div className="fup-pending__price">{fIDR(paymentTarget.amount)}</div>
              {paymentTarget.isPaid && <span className="badge bg-success mt-1">Lunas</span>}
            </div>
          </div>
          <div className="card-body p-0">
            {paymentTarget.isPaid ? (
              <div className="p-4 text-center">
                <i className="fa fa-check-circle text-success mb-2" style={{ fontSize: "3rem" }} />
                <h5 className="mb-1">Pembayaran Berhasil</h5>
                <p className="text-muted mb-0">Paket add-on ini telah dibayar dan aktif.</p>
              </div>
            ) : (
              <PaymentMethodPickerAddOn purchase={paymentTarget} profile={profile} />
            )}
          </div>
        </div>
      )}

      {/* Package picker (shown when nothing pending, or user chose "lihat paket lain") */}
      {!showPaymentScreen && (
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between">
            <span className="fw-semibold" style={{ fontSize: 14 }}>Beli Quota</span>
            {browsing && hasPendingPurchase && !pendingPurchase.isPaid && (
              <button 
                type="button" 
                className="fup-pending__switch" 
                onClick={() => {
                  setBrowsing(false)
                  setActivePurchase(pendingPurchase)
                }}
              >
                Kembali ke pembayaran
              </button>
            )}
          </div>
          <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {availableTabs.length > 1 && (
              <div className="fup-tabs">
                {availableTabs.map(t => (
                  <button
                    key={t.key}
                    type="button"
                    className={`fup-tab${tab === t.key ? " is-active" : ""}`}
                    onClick={() => setTab(t.key)}
                  >
                    <i className={`fa ${t.icon}`} /> {t.label}
                  </button>
                ))}
              </div>
            )}

            {loading ? (
              <div className="fup-skeleton">Memuat paket...</div>
            ) : filteredOptions.length ? (
              <div className="fup-grid">
                {filteredOptions.map(option => {
                  const isSelected = selectedId === option.id
                  const hasDiscount = Number(option.discount) > 0 && option.finalPrice != null && option.finalPrice !== option.price
                  return (
                    <button
                      key={option.id}
                      type="button"
                      className={`fup-card${isSelected ? " is-selected" : ""}`}
                      onClick={() => setSelectedId(prev => (prev === option.id ? "" : option.id))}
                    >
                      <span className="fup-card__check"><i className="fa fa-check" /></span>
                      <span className="fup-card__tag">{option.addOnTypeLabel || "Add-On"}</span>
                      <span className="fup-card__name">{option.name}</span>
                      <span className="fup-card__metric">
                        <i className="fa fa-bolt" /> {addOnMetric(option)}
                      </span>
                      <span className="fup-card__price-row">
                        <span>
                          {hasDiscount && <span className="fup-card__strike">{fIDR(option.price)}</span>}
                          <span className="fup-card__price">{fIDR(option.finalPrice ?? option.price)}</span>
                        </span>
                      </span>
                    </button>
                  )
                })}
              </div>
            ) : (
              <div className="fup-empty">Belum ada paket Add-On yang tersedia untuk layanan Anda.</div>
            )}
          </div>

          {selectedPackage && (
            <div className="card-body pt-0">
              <div className="fup-buybar">
                <div className="fup-buybar__info">
                  <div className="fup-buybar__label">Paket dipilih</div>
                  <div className="fup-buybar__name">{selectedPackage.name}</div>
                </div>
                <div className="fup-buybar__price">{fIDR(selectedPackage.finalPrice ?? selectedPackage.price)}</div>
                <button
                  type="button"
                  className="fup-buybar__cta"
                  disabled={submitting}
                  onClick={createPurchase}
                >
                  {submitting ? "Memproses..." : "Beli Sekarang"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* History */}
      <div className="card">
        <div className="card-header">
          <span className="fw-semibold" style={{ fontSize: 14 }}>Riwayat Transaksi Add-On</span>
        </div>
        <div className="card-body">
          {!purchases.length ? (
            <div className="fup-empty">Belum ada transaksi Add-On.</div>
          ) : (
            <div className="fup-history">
              {purchases.map(item => (
                <div className="fup-history__item" key={item.id}>
                  <div className="fup-history__top">
                    <div>
                      <div className="fup-history__name">{item.addOnName}</div>
                      <div className="fup-history__id">{item.id}</div>
                      <div className="fup-history__type">{item.addOnTypeLabel || "Add-On"} · {paymentLabel(item)}</div>
                    </div>
                    <span className={`fup-status ${item.isPaid ? "fup-statuspaid" : "fup-statusunpaid"}`}>
                      {item.isPaid ? "Lunas" : "Belum Lunas"}
                    </span>
                  </div>
                  <div className="fup-history__bottom d-flex align-items-center justify-content-between mt-2">
                    <div>
                      <span className="fup-history__date d-block">{DateFormatter(item.creation)}</span>
                      <span className="fup-history__amount d-block fw-semibold">{fIDR(item.amount)}</span>
                    </div>
                    <button 
                      type="button" 
                      className={`fup-history__pay ${item.isPaid ? "btn btn-sm btn-outline-secondary" : ""}`} 
                      onClick={() => openPaymentFor(item)}
                    >
                      {item.isPaid ? "Lihat Detail" : "Bayar Sekarang"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FupQuotaContent